/**
 * Composable appointment actions (Edit / Reschedule / Cancel / No-show).
 *
 * Each action is exported as an individual button component so consumers can
 * arrange them freely, and `AppointmentActionsMenu` bundles them into a kebab
 * dropdown for tight layouts. Edit and Reschedule are gated on
 * `canEditAppointment`; Cancel is gated on `canCancelAppointment`.
 */

import { CalendarSync, EllipsisVertical, Pencil, Stethoscope, UserX, X } from 'lucide-react'
import { type ComponentProps, useState } from 'react'
import { Link } from 'react-router-dom'

import type { AppointmentWithRelations } from '@snootsvet/shared'
import {
  canCancelAppointment,
  canChargeLateChangeFee,
  canChargeNoShowFee,
  canEditAppointment,
  formatClinicAppointmentPenaltyFee,
} from '@snootsvet/shared'

import { FormDescription, FormLabel } from '@/frontend/components/form/primitives'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/frontend/components/ui/alert-dialog'
import { Button } from '@/frontend/components/ui/button'
import { Checkbox } from '@/frontend/components/ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/frontend/components/ui/dropdown-menu'
import {
  AppointmentForm,
  appointmentToFormValues,
} from '@/frontend/features/appointments/appointment.form'
import { usePatchAppointment } from '@/frontend/hooks/api/use-patch-appointment'
import { getAppointmentCalendarUrl } from '@/frontend/lib/appointments'

const { VITE_CUSTOMER_APP_URL } = import.meta.env

type ButtonProps = ComponentProps<typeof Button>

interface EditProps {
  appointment: AppointmentWithRelations
  timeZone: string
  /**
   * Overrides the built-in edit dialog. When provided, clicking Edit calls
   * this instead — used by the calendar, which drives editing through its own
   * modal state (with drag params).
   */
  onEdit?: (appointment: AppointmentWithRelations) => void
}

interface CancelProps {
  appointment: AppointmentWithRelations
}

interface NoShowProps {
  appointment: AppointmentWithRelations
  onDone?: () => void
}

function getAppointmentPenaltyUnavailableReason(appointment: {
  patient: { owner: Pick<AppointmentWithRelations['patient']['owner'], 'stripeCustomerId'> }
}): string | null {
  if (!appointment.patient.owner.stripeCustomerId) {
    return 'This member is not linked to Stripe, so the fee cannot be taken.'
  }
  return null
}

function useNoShowAppointment({ appointment, onDone }: NoShowProps) {
  const [open, setOpen] = useState(false)
  const feeEligible = canChargeNoShowFee(appointment)
  const unavailableReason = getAppointmentPenaltyUnavailableReason(appointment)
  const [checked, setChecked] = useState(feeEligible && !unavailableReason)
  const { mutate: patchAppointment, isPending } = usePatchAppointment()

  const trigger = () => {
    setChecked(feeEligible && !unavailableReason)
    setOpen(true)
  }

  const dialog = (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Mark as no-show?</AlertDialogTitle>
          <AlertDialogDescription>
            This will mark the appointment as a no-show.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {feeEligible && appointment.clinicId && (
          <div className="space-y-2">
            <FormLabel className="flex items-center gap-2">
              <Checkbox
                checked={checked}
                onCheckedChange={setChecked}
                disabled={isPending || Boolean(unavailableReason)}
              />
              Charge {formatClinicAppointmentPenaltyFee(appointment.clinicId)} no-show fee
            </FormLabel>
            <FormDescription>
              {unavailableReason ??
                'If selected, payment will be attempted using the member’s saved card.'}
            </FormDescription>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={(event) => {
              event.preventDefault()
              patchAppointment(
                {
                  id: appointment.id,
                  status: 'no-show',
                  chargeAppointmentPenalty: checked && feeEligible && !unavailableReason,
                },
                {
                  onSuccess: () => {
                    setOpen(false)
                    onDone?.()
                  },
                }
              )
            }}
            disabled={isPending}
          >
            {isPending ? 'Marking…' : 'Mark as no-show'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )

  return { trigger, dialog, isPending }
}

function useEditAppointment({ appointment, timeZone, onEdit }: EditProps) {
  const [open, setOpen] = useState(false)
  const available = canEditAppointment(appointment)

  const trigger = () => {
    if (onEdit) {
      onEdit(appointment)
    } else {
      setOpen(true)
    }
  }

  const dialog = onEdit ? null : (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Appointment</DialogTitle>
        </DialogHeader>
        <AppointmentForm
          defaultValues={appointmentToFormValues(appointment, timeZone)}
          timeZone={timeZone}
          appointmentId={appointment.id}
          patientDetails={{
            name: appointment.patient.name,
            owner: appointment.patient.owner,
          }}
          onSuccess={() => setOpen(false)}
          onCancel={() => setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  )

  return { available, trigger, dialog }
}

function useCancelAppointment({ appointment }: CancelProps) {
  const [open, setOpen] = useState(false)
  const [notifyMember, setNotifyMember] = useState(true)
  const feeEligible = canChargeLateChangeFee(appointment)
  const unavailableReason = getAppointmentPenaltyUnavailableReason(appointment)
  const [checked, setChecked] = useState(false)
  const { mutate: patchAppointment, isPending } = usePatchAppointment()
  const available = canCancelAppointment(appointment)

  const trigger = () => {
    setNotifyMember(new Date(appointment.startTime) >= new Date())
    setChecked(false)
    setOpen(true)
  }

  const dialog = (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel appointment?</AlertDialogTitle>
          <AlertDialogDescription>
            This will mark the appointment as cancelled. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="flex flex-col gap-3">
          <FormLabel htmlFor="cancel-notify-member" className="flex items-center gap-2">
            <Checkbox
              id="cancel-notify-member"
              checked={notifyMember}
              onCheckedChange={setNotifyMember}
            />
            Notify pet owner by email
          </FormLabel>

          {feeEligible && appointment.clinicId && (
            <div className="space-y-2">
              <FormLabel className="flex items-center gap-2">
                <Checkbox
                  checked={checked}
                  onCheckedChange={setChecked}
                  disabled={isPending || Boolean(unavailableReason)}
                />
                Charge {formatClinicAppointmentPenaltyFee(appointment.clinicId)} late-cancellation
                fee
              </FormLabel>
              {unavailableReason ? (
                <FormDescription>{unavailableReason}</FormDescription>
              ) : (
                <div className="space-y-2">
                  <FormDescription>
                    Charge this fee when a member cancels late. Leave it unchecked where Snoots
                    needs to cancel for administrative reasons, such as staff illness, or where the
                    member has a valid exceptional reason for cancelling late.
                  </FormDescription>
                  <FormDescription>
                    Payment will be attempted using the member’s saved card.
                  </FormDescription>
                </div>
              )}
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Keep appointment</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={(event) => {
              event.preventDefault()
              patchAppointment(
                {
                  id: appointment.id,
                  status: 'cancelled',
                  notifyMember,
                  chargeAppointmentPenalty: checked && feeEligible && !unavailableReason,
                },
                { onSuccess: () => setOpen(false) }
              )
            }}
            disabled={isPending}
          >
            {isPending ? 'Cancelling…' : 'Cancel appointment'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )

  return { available, trigger, dialog }
}

function rescheduleAvailable(appointment: AppointmentWithRelations) {
  return canEditAppointment(appointment) && Boolean(appointment.bookingToken)
}

function openRescheduleLink(appointment: AppointmentWithRelations) {
  window.open(`${VITE_CUSTOMER_APP_URL}/booking?token=${appointment.bookingToken}`, '_blank')
}

export function EditAppointmentAction({
  appointment,
  timeZone,
  onEdit,
  variant = 'default',
  size = 'lg',
  ...buttonProps
}: EditProps & Omit<ButtonProps, keyof EditProps>) {
  const { available, trigger, dialog } = useEditAppointment({ appointment, timeZone, onEdit })
  if (!available) return null
  return (
    <>
      <Button variant={variant} size={size} onClick={trigger} {...buttonProps}>
        <Pencil />
        Edit
      </Button>
      {dialog}
    </>
  )
}

export function CancelAppointmentAction({
  appointment,
  variant = 'destructive',
  size = 'lg',
  ...buttonProps
}: CancelProps & Omit<ButtonProps, keyof CancelProps>) {
  const { available, trigger, dialog } = useCancelAppointment({ appointment })
  if (!available) return null
  return (
    <>
      <Button variant={variant} size={size} onClick={trigger} {...buttonProps}>
        <X />
        Cancel
      </Button>
      {dialog}
    </>
  )
}

export function NoShowAppointmentAction({
  appointment,
  onDone,
  variant = 'ghost-destructive',
  size,
  disabled,
  ...buttonProps
}: NoShowProps & Omit<ButtonProps, keyof NoShowProps>) {
  const { trigger, dialog, isPending } = useNoShowAppointment({ appointment, onDone })
  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={trigger}
        disabled={isPending || disabled}
        {...buttonProps}
      >
        <UserX className="size-3" />
        No-show
      </Button>
      {dialog}
    </>
  )
}

interface AppointmentActionsMenuProps extends EditProps {
  /** Size of the kebab trigger button. Defaults to 'icon-sm'. */
  triggerSize?: 'icon-sm' | 'icon-xs'
  /**
   * Adds a Create Consultation item above Edit/Reschedule/Cancel. The patient
   * timeline passes it on appointments dated in the future, where the action is
   * demoted out of the card so starting one early takes a deliberate second click.
   * Omit it and the item isn't rendered.
   */
  onCreateConsultation?: () => void
}

export function AppointmentActionsMenu({
  appointment,
  timeZone,
  onEdit,
  triggerSize = 'icon-sm',
  onCreateConsultation,
}: AppointmentActionsMenuProps) {
  const edit = useEditAppointment({ appointment, timeZone, onEdit })
  const cancel = useCancelAppointment({ appointment })
  const canReschedule = rescheduleAvailable(appointment)

  if (!edit.available && !cancel.available && !onCreateConsultation && !appointment.clinicId)
    return null

  const iconSize = triggerSize === 'icon-xs' ? 'size-3.5' : 'size-4'

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger render={<Button variant="ghost" size={triggerSize} />}>
          <EllipsisVertical className={iconSize} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {onCreateConsultation && (
            <DropdownMenuItem onClick={onCreateConsultation}>
              <Stethoscope className="size-3.5" />
              Create Consultation
            </DropdownMenuItem>
          )}
          {onCreateConsultation &&
            (appointment.clinicId || edit.available || canReschedule || cancel.available) && (
              <DropdownMenuSeparator />
            )}
          {appointment.clinicId && (
            <DropdownMenuItem
              render={
                <Link to={getAppointmentCalendarUrl(appointment.clinicId, appointment.startTime)} />
              }
            >
              <CalendarSync className="size-3.5" />
              View on calendar
            </DropdownMenuItem>
          )}
          {edit.available && (
            <DropdownMenuItem onClick={edit.trigger}>
              <Pencil className="size-3.5" />
              Edit
            </DropdownMenuItem>
          )}
          {canReschedule && (
            <DropdownMenuItem onClick={() => openRescheduleLink(appointment)}>
              <CalendarSync className="size-3.5" />
              Reschedule
            </DropdownMenuItem>
          )}
          {cancel.available && (
            <>
              {edit.available && <DropdownMenuSeparator />}
              <DropdownMenuItem variant="destructive" onClick={cancel.trigger}>
                <X className="size-3.5" />
                Cancel
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {edit.dialog}
      {cancel.dialog}
    </>
  )
}
